import * as math from 'mathjs';
import { sanitizeExpression } from '../sanitizeExpression';

const EPS = 1e-8;

function nearlyZero(value) {
  return Math.abs(Number(value || 0)) < EPS;
}

function evaluateEquationPolynomial(input) {
  const [lhs, rhs] = String(input || '').split('=');
  if (!lhs || rhs === undefined) return null;

  try {
    const rawExpr = `(${lhs})-(${rhs})`.replace(/xy/g, 'x*y').replace(/yx/g, 'y*x');
    const expr = sanitizeExpression(rawExpr);
    const compiled = math.compile(expr);
    const valueAt = (x, y) => Number(compiled.evaluate({ x, y }));

    const f00 = valueAt(0, 0);
    const f10 = valueAt(1, 0);
    const fm10 = valueAt(-1, 0);
    const f01 = valueAt(0, 1);
    const f0m1 = valueAt(0, -1);
    const f11 = valueAt(1, 1);
    if (![f00, f10, fm10, f01, f0m1, f11].every(Number.isFinite)) return null;

    const x2 = (f10 + fm10 - 2 * f00) / 2;
    const x = (f10 - fm10) / 2;
    const y2 = (f01 + f0m1 - 2 * f00) / 2;
    const y = (f01 - f0m1) / 2;
    const xy = f11 - f00 - x2 - y2 - x - y;

    return { x2, y2, xy, x, y, c: f00 };
  } catch {
    return null;
  }
}

export function generateCircleData(input, step = 0.01) {
  const clean = input.replace(/\s+/g, '');
  const simplified = clean.match(/^x\^2\+y\^2=(\d+(\.\d+)?)$/);
  if (simplified) {
    const r = Math.sqrt(parseFloat(simplified[1]));
    const x = [], y = [];
    for (let t = 0; t <= 2 * Math.PI; t += step) {
      x.push(r * Math.cos(t));
      y.push(r * Math.sin(t));
    }
    return [{ x, y }];
  }
  const match = clean.match(/^\(x([+-]\d+(\.\d+)?)\)\^2\+\(y([+-]\d+(\.\d+)?)\)\^2=(\d+(\.\d+)?)$/);
  if (!match) return [];
  const a = -parseFloat(match[1]);
  const b = -parseFloat(match[3]);
  const r = Math.sqrt(parseFloat(match[5]));
  const x = [], y = [];
  for (let t = 0; t <= 2 * Math.PI; t += step) {
    x.push(a + r * Math.cos(t));
    y.push(b + r * Math.sin(t));
  }
  return [{ x, y }];
}

export function convertGeneralToCircle(input) {
  const coeffs = evaluateEquationPolynomial(input);
  if (!coeffs) return null;
  if (nearlyZero(coeffs.x2) || nearlyZero(coeffs.y2)) return null;
  if (!nearlyZero(coeffs.xy)) return null;
  if (!nearlyZero(coeffs.x2 - coeffs.y2)) return null;

  const scale = coeffs.x2;
  const D = coeffs.x / scale;
  const E = coeffs.y / scale;
  const F = coeffs.c / scale;
  const a = -D / 2;
  const b = -E / 2;
  const radiusSquared = a * a + b * b - F;
  if (radiusSquared < -EPS) return null;
  const r = Math.sqrt(Math.max(0, radiusSquared));
  if (!Number.isFinite(r)) return null;
  return { a, b, r };
}
